import { useMemo } from 'react';

import { Skeleton, Box } from '@cast/design-system';

import { isNonEmptyArray } from 'utils/types/non-empty-array';

import { PreparedNode } from '../../types';
import { BinPackingBlockedNodesSummary } from './_components/BinPackingBlockedNodesSummary';
import { NodesResourceOfferingsBreakdown } from './_components/NodesResourceOfferingsBreakdown';
import { NodesStatusesBreakdown } from './_components/NodesStatusesBreakdown';
import { SummaryItem } from './_components/SummaryItem';
import { checkBinPackingBlock } from '../../../_utils/common';

type LayoutProps = {
  leftColumn: React.ReactElement;
  middleColumn: React.ReactElement;
  rightColumn: React.ReactElement | null;
};

const Layout = ({ leftColumn, middleColumn, rightColumn }: LayoutProps) => {
  return (
    <Box
      data-testid="nodes-summary"
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: rightColumn ? '1fr max-content 1fr' : '1fr 1fr',
        gap: 24,
        overflow: 'auto',
      }}
    >
      {leftColumn}
      {middleColumn}
      {rightColumn}
    </Box>
  );
};

type Props = {
  nodes: PreparedNode[];
};

export const Summary = ({ nodes }: Props) => {
  const binPackingBlockedNodes = useMemo(() => {
    return nodes.filter((node) => {
      const { isBinPackingBlocked } = checkBinPackingBlock(node);
      return isBinPackingBlocked;
    });
  }, [nodes]);

  return (
    <Layout
      leftColumn={<NodesResourceOfferingsBreakdown nodes={nodes} />}
      middleColumn={<NodesStatusesBreakdown nodes={nodes} />}
      rightColumn={
        isNonEmptyArray(binPackingBlockedNodes) ? (
          <BinPackingBlockedNodesSummary
            binPackingBlockedNodes={binPackingBlockedNodes}
          />
        ) : null
      }
    />
  );
};

const SummarySkeleton = () => {
  return (
    <Layout
      leftColumn={
        <SummaryItem>
          <Skeleton height="100%" />
        </SummaryItem>
      }
      middleColumn={
        <SummaryItem>
          <Skeleton height="100%" />
        </SummaryItem>
      }
      rightColumn={null}
    />
  );
};

Summary.Skeleton = SummarySkeleton;
